/**
 * Adapter from Teslyn entities to the TJR Job Summary inputs (tjr-summary.ts), so the summary page is computed
 * from the reviewed line status (ADR-013) with the formulas verified against TJR's printed reports.
 *
 * - Daily km (`km_flown_m`, `km_accepted_m`) are not typed by the user (spec 4.3): they are the sums of the
 *   entry's flights' line status. Flown km counts every status on the km-flown basis of decision 32
 *   (`flownCreditM`: planned length when fully flown, logged length when partial); accepted km counts reviewed
 *   (non-pending), `use_km = true` statuses only. Overfly is on the same km-flown basis (issue #11): TJR's KM Overfly
 *   is its km flown less the planned length, so the log's run past both ends of a line adds nothing (F26048 12 Oct:
 *   18.80 from the real logs, 30.25 from the logged lengths).
 * - A status belongs to the daily entry of its flight (`isEntryOfFlight`: same date, same flight number and the same
 *   aircraft when both have one). The contract's `tjrFromTeslyn(entries, statuses, plan)` has no way to make that
 *   link, so `flights` is a fourth argument. A mobilisation entry is paired with its flight like any other but never
 *   counts as survey (decision 722): it has no line status, so its km are 0.
 * - The TJR plan holds original lines only (version digit 0, not a reflight or patch); a reflight or patch has no
 *   planned length of its own, so it never adds to the total planned km (decision 29).
 * - `remainingKm` is Teslyn's "Est KM Remaining" (`planProgress`, decision 29): original lines not accepted in full,
 *   an open patch counted at its own length.
 * - `flownKm` and `acceptedKm` are the project totals straight from line status (issue #11, ADR-013): every live
 *   status passed in, whether or not a daily entry has been saved for its flight. Pass the inputs as `jobSummary`'s
 *   `fromLines`.
 */
import { formatTjrDate } from './dates.js';
import { parseLineNumber } from './lines.js';
import { withCompletedBlocks } from './block-report.js';
import type { Block, DailyEntry, EntryType, Flight, LineStatus, PlannedLine } from './model.js';
import { flownCreditM, planProgress } from './progress.js';
import { plannedLineRole } from './reflights.js';
import { ENTRY_TYPE } from './tjr-summary.js';
import type { LineStatusKm, TjrDailyEntry, TjrLineStatus, TjrPlanLengths } from './tjr-summary.js';

/** `jobSummary`'s inputs; the object itself is its `fromLines` (flown, accepted and remaining km). */
export interface TjrSummaryInputs extends LineStatusKm {
  readonly daily: TjrDailyEntry[];
  readonly lines: TjrLineStatus[];
  readonly plan: TjrPlanLengths;
}

const TJR_ENTRY_TYPE: Readonly<Record<EntryType, number>> = ENTRY_TYPE;

function isoToTjrDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new RangeError(`invalid date "${date}"`);
  return formatTjrDate(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
}

/**
 * The live line status of each daily entry's flights, by entry id: a status belongs to the entry of its flight
 * (`isEntryOfFlight`). Non-flight entries have none, and so do mobilisation entries: a mobilisation flight never
 * counts as survey (decision 722), so its km and lines stay out of the daily km, the Day view, the Daily entry table
 * and CSV, the pilots' km, the period reviews and the Job Summary. The Cloud Flights page pairs the entry with its
 * flight itself (`flightRows`) and shows the flight's own lines.
 */
export function entryLineStatuses(
  entries: readonly DailyEntry[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): Map<string, LineStatus[]> {
  const byFlight = new Map<string, LineStatus[]>();
  for (const s of statuses) {
    if (s.deleted_at !== null) continue;
    const list = byFlight.get(s.flight_id) ?? [];
    list.push(s);
    byFlight.set(s.flight_id, list);
  }
  const out = new Map<string, LineStatus[]>();
  for (const e of entries) {
    if (e.deleted_at !== null) continue;
    // decision 722: paired with its flight, but a mobilisation flight's lines are not survey
    if (e.mobilization) {
      out.set(e.id, []);
      continue;
    }
    out.set(
      e.id,
      flights.filter((f) => isEntryOfFlight(e, f)).flatMap((f) => byFlight.get(f.id) ?? []),
    );
  }
  return out;
}

/**
 * Whether a live daily entry is the entry of a live flight: a flight entry with the flight's date and number, and its
 * aircraft when both have one (decision 486). The one rule Teslyn pairs an entry and its flight by (decision 722):
 * the line statuses of an entry (`entryLineStatuses`), the calibrations' daily entry (`resolveCalEntries`), the
 * daily entry's own flight (`flightOfEntry`) and Teslyn Cloud's Flights page. A mobilisation entry pairs like any
 * other; what it must not count, the survey figures leave out themselves. An entry with no flight number, or flight
 * 0, pairs with no flight: a ferry day typed without a number (8 and 9 Oct 2026 of F26048) has no log, and the
 * `.tjf` import and the flight import never make a flight 0.
 */
export function isEntryOfFlight(
  e: Pick<DailyEntry, 'deleted_at' | 'entry_type' | 'flt_num' | 'date' | 'aircraft_id'>,
  f: Pick<Flight, 'deleted_at' | 'date' | 'flt_num' | 'aircraft_id'>,
): boolean {
  if (e.deleted_at !== null || f.deleted_at !== null) return false;
  if (e.entry_type !== 'flight' || e.flt_num === null || e.flt_num <= 0) return false;
  return (
    f.date === e.date &&
    f.flt_num === e.flt_num &&
    (e.aircraft_id === null || f.aircraft_id === null || f.aircraft_id === e.aircraft_id)
  );
}

/** Accepted metres of statuses: reviewed (not pending) and `use_km`, the `reportTotals` rule. */
export function acceptedM(
  statuses: readonly Pick<LineStatus, 'acceptance' | 'use_km' | 'accepted_m'>[],
): number {
  return statuses.filter((s) => s.acceptance !== 'pending' && s.use_km).reduce((a, s) => a + s.accepted_m, 0);
}

export function tjrFromTeslyn(
  entries: readonly DailyEntry[],
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  flights: readonly Flight[],
  /** N32: blocks marked "km completed" count 0 km remaining; omitted, none. */
  blocks: readonly Pick<Block, 'id' | 'km_completed'>[] = [],
): TjrSummaryInputs {
  const live = statuses.filter((s) => s.deleted_at === null);
  const ofEntry = entryLineStatuses(entries, live, flights);

  const daily: TjrDailyEntry[] = entries
    .filter((e) => e.deleted_at === null)
    .map((e) => {
      const own = ofEntry.get(e.id) ?? [];
      return {
        date: isoToTjrDate(e.date),
        flt_num: e.flt_num ?? 0,
        entry_type: TJR_ENTRY_TYPE[e.entry_type],
        days_alloc: e.days_alloc,
        svy_hours: e.svy_hours,
        fuel_used_l: e.fuel_used_l,
        km_flown_m: own.reduce((a, s) => a + flownCreditM(s), 0),
        km_accepted_m: acceptedM(own),
        mobilization: e.mobilization,
      };
    });

  const lines: TjrLineStatus[] = live.map((s) => ({
    flown: true,
    line_number: s.line_number,
    use_km: s.use_km,
    // the km-flown basis (decision 32): TJR's overfly is km flown − planned length, not log length − planned
    flown_m: flownCreditM(s),
    accepted_m: s.acceptance === 'pending' ? 0 : s.accepted_m,
    refly_reason: s.reason,
  }));

  const lengths: Record<string, number> = {};
  for (const p of plan) {
    if (p.deleted_at !== null || parseLineNumber(p.number).version !== 0) continue;
    if (plannedLineRole(p) !== 'original') continue;
    lengths[String(p.number)] = p.length_m / 1000;
  }
  return {
    daily,
    lines,
    plan: lengths,
    flownKm: live.reduce((a, s) => a + flownCreditM(s), 0) / 1000,
    acceptedKm: acceptedM(live) / 1000,
    remainingKm: withCompletedBlocks(planProgress(live, plan), blocks).remainingM / 1000,
  };
}
